import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { difference, intersection } from 'lodash-es';

import { ReactComponent as TickIcon } from '../../../../assets/images/greenTickIcon.svg';
import { ReactComponent as LabelIcon } from '../../../../assets/images/labelIcon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/images/plusWithoutCircleIcon.svg';
import { ReactComponent as TrashIcon } from '../../../../assets/images/trashcanIcon.svg';
import { ReactComponent as VectorIcon } from '../../../../assets/images/vectorIcon.svg';
import Tooltip from '../../../../components/shared/Tooltip/Tooltip';
import { areEqual } from '../../../../helpers/commonHelper';
import { createTag, getTags, updateTag } from '../../../../redux/actions/networkActions';
import CreateTagModal from '../CreateTagModal/CreateTagModal';

import styles from './BulkActionMenu.module.scss';

const delay = { show: '600', hide: '600' };

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <button
    type="button"
    className={styles.dropDownButton}
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
  >
    {children}
  </button>
));

const BulkActionMenu = ({
  selectedContacts,
  handleToggleDeleteModal,
  handleToggleMergeModal,
  contacts,
  tagSuccessCallback,
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedContactsDetails, setSelectedContactsDetails] = useState([]);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [dropDownToggle, setDropDownToggle] = useState('outside');
  const tags = useSelector((state) => state.networkReducer.tags);

  const commonSameTags = useMemo(() => {
    if (!selectedContactsDetails) {
      return [];
    }

    const commonTags = selectedContactsDetails.map(({ taggings_attributes }) =>
      taggings_attributes.map(({ general_tags_attributes }) => general_tags_attributes.tag_id)
    );

    return intersection(...commonTags);
  }, [selectedContactsDetails]);

  const handleSelectedTags = useCallback(
    (id) => {
      if (selectedTags.includes(id)) {
        setSelectedTags(selectedTags.filter((tag) => tag !== id));
        if (commonSameTags.includes(id)) {
          setTagsToDelete([...tagsToDelete, id]);
        }
        return;
      }
      setSelectedTags([...selectedTags, id]);
      setTagsToDelete(tagsToDelete.filter((item) => item !== id));
    },
    [selectedTags, tagsToDelete, commonSameTags]
  );

  useEffect(() => {
    setSelectedTags(commonSameTags);
  }, [commonSameTags]);

  const handleToggleCreateModal = useCallback(() => {
    setShowCreateModal((prevState) => !prevState);
  }, []);

  const createTagSuccessCallback = useCallback(() => {
    tagSuccessCallback();
    dispatch(getTags(true));
    setDropDownToggle(true);
  }, []);

  const updateTags = useCallback(() => {
    const tagsToAdd = difference(selectedTags, commonSameTags);
    dispatch(updateTag(selectedContacts, tagsToAdd, tagsToDelete, createTagSuccessCallback));
  }, [selectedContacts, selectedTags, tagsToDelete, commonSameTags]);

  const handleAddTag = useCallback(
    (tagValue) => {
      dispatch(createTag(selectedContacts, tagValue, createTagSuccessCallback));
      handleToggleCreateModal();
    },
    [selectedContacts]
  );

  useEffect(() => {
    const contactDetailsArray = [];
    selectedContacts.forEach((selectedId) => {
      const fullRow = contacts.find(({ id }) => selectedId === id);
      contactDetailsArray.push(fullRow);
    });
    setSelectedContactsDetails(contactDetailsArray);
  }, [selectedContacts, contacts]);

  return (
    <div className={styles.bulkActionMenu}>
      <Tooltip text={t('delete')} placement="bottom" delay={delay}>
        <TrashIcon onClick={handleToggleDeleteModal} className={classNames(styles.actionIcon, styles.trashIcon)} />
      </Tooltip>
      {selectedContacts.length > 1 && (
        <Tooltip text={t('bulkActions.merge')} placement="bottom" delay={delay}>
          <VectorIcon onClick={handleToggleMergeModal} className={styles.actionIcon} />
        </Tooltip>
      )}

      <Dropdown autoClose={dropDownToggle}>
        <Dropdown.Toggle as={CustomToggle}>
          <Tooltip text={t('bulkActions.manageTags')} placement="bottom" delay={delay}>
            <LabelIcon className={styles.actionIcon} />
          </Tooltip>
        </Dropdown.Toggle>
        <CreateTagModal
          showModal={showCreateModal}
          handleToggleCreateModal={handleToggleCreateModal}
          handleAddTag={handleAddTag}
        />
        <Dropdown.Menu className={styles.dropdownMenu} align="end">
          <Dropdown.Item bsPrefix={styles.dropdownLabel}>{t('bulkActions.manageTags')}</Dropdown.Item>

          <div className={styles.DropdownDiv}>
            {tags.map((tag) => (
              <Dropdown.Item bsPrefix={styles.dropdownLabel} key={tag.id} onClick={() => handleSelectedTags(tag.id)}>
                <div className={styles.tags}>
                  <LabelIcon />
                  <span className={styles.tagLabel}>{tag.name}</span>
                  {selectedTags.includes(tag.id) && <TickIcon className={styles.tickIcon} />}
                </div>
              </Dropdown.Item>
            ))}
          </div>

          {areEqual(selectedTags, commonSameTags) ? (
            <Dropdown.Item bsPrefix={styles.dropdownLabel} onClick={handleToggleCreateModal}>
              <div className={styles.tags}>
                <PlusIcon />
                <span className={styles.tagLabel}>{t('bulkActions.createNew')}</span>
              </div>
            </Dropdown.Item>
          ) : (
            <Dropdown.Item bsPrefix={styles.dropdownLabel} onClick={updateTags}>
              <div className={styles.tags}>
                <span className={styles.tagLabel}>{t('apply')}</span>
              </div>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default BulkActionMenu;
